/**
 * Design constants for UI styling
 *
 * IMPORTANT: Branding vs Default Colors
 * - Branded colors should use Tailwind semantic classes (bg-primary, text-primary)
 *   that reference CSS variables set by BrandingProvider
 * - Default colors are fallbacks when branding is unavailable
 * - Structural colors remain constant regardless of branding
 * - Brand colors are the canonical ZonedLabs/golfrecruit.ai palette (not overridable)
 */

export const DESIGN_COLORS = {
  // Default colors (fallback when branding unavailable)
  default: {
    primary: {
      green: "#65C109",
      greenDark: "#4a9007",
      greenDarker: "#357005",
    },
    background: {
      lightGreen: "#f5f9f3",
      lightGreenAlt: "#e8f5d8",
      veryLightGreen: "#f0f9e8",
      white: "#ffffff",
    },
  },
  // Structural colors (not brandable)
  structural: {
    text: {
      dark: "#111f11",
      muted: "#6b7270",
    },
    border: {
      subtle: "rgba(17,31,17,0.1)",
      primary: "rgba(101,193,9,0.2)",
    },
  },
  // Canonical brand palette (not overridable by white-label)
  brand: {
    forest: "#1A2B23",
    charcoal: "#2D3A32",
    lime: "#65C109",
    sage: "#CED9C3",
    mint: "#B8D4A2",
    placeholder: "#7A9B8A",
  },
  // Fit tier colors (ZON-280, approved 2026-10-03): Dream amber, Realistic lime, Safety blue.
  // Tailwind: bg-amber-soft/text-amber-text, bg-lime-soft/text-primary-text, bg-safety-soft/text-safety-text.
  fitTier: {
    dream: { text: "#7A6400", bg: "#FBF3D5", accent: "#E2C94E" },
    realistic: { text: "#3E7F06", bg: "#EFF8E8", accent: "#65C109" },
    safety: { text: "#2C6699", bg: "#E3ECF7", accent: "#3E86C6" },
  },
} as const;

/** One page-title style for every end-user page (Fraunces, 28px on phones, 32px up). */
export const PAGE_TITLE_CLASS =
  "font-display text-[28px] font-extrabold leading-none tracking-[-0.04em] text-forest sm:text-[32px]";
export const PAGE_SUBTITLE_CLASS =
  "mt-2 max-w-2xl text-[13px] leading-relaxed text-muted-foreground";

export const DESIGN_SPACING = {
  header: {
    height: "89px",
    logoHeight: "56px",
  },
  padding: {
    card: "24px",
    section: "8px",
    button: "6px",
    small: "4px",
    xs: "3px",
    xxs: "2px",
  },
  borderRadius: {
    card: "12px",
    input: "8px",
  },
} as const;

export const DESIGN_SIZING = {
  button: {
    minWidth: "82px",
  },
  select: {
    maxWidth: "220px",
  },
  label: {
    minWidth: "180px",
  },
  icon: {
    small: "8px",
    badge: "28px",
  },
  typography: {
    letterTracking: "0.07px",
  },
} as const;
